import React, { useState, useEffect } from 'react';
import { useForm, usePage, Link } from '@inertiajs/react';
import { Save, ArrowLeft, Image as ImageIcon, AlertCircle } from 'lucide-react';

interface PageProps {
  invitation: { id: number; name: string; whatsapp_template_sid: string };
  template?: any;
  whatsappTemplateDetails?: any;
}

const AVAILABLE_VARIABLES = [
  { value: 'name', label: 'Invitee Name' },
  { value: 'title_name', label: 'Title + Name' },
  { value: 'email', label: 'Email Address' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'company', label: 'Company / Organization' },
  { value: 'position', label: 'Position / Job Title' },
  { value: 'event_name', label: 'Event Name' },
  { value: 'qr_code', label: 'QR Code Link' },
  { value: 'rsvp_link', label: 'RSVP Link' },
  { value: 'custom', label: 'Custom Excel Column...' }
];

export default function TemplateConfig() {
  const { invitation, template, whatsappTemplateDetails } = usePage<PageProps>().props;
  
  // Extract details from backend response
  const details = whatsappTemplateDetails?.success ? whatsappTemplateDetails : null;
  const variables = details?.variables || [];
  const bodyText = details?.body || '';
  const headerFormat = details?.raw_data?.components?.find((c: any) => c.type === 'HEADER')?.format;
  const buttons = details?.actions || [];
  const footerText = details?.subtitle || '';

  // Initialize variable mapping from existing DB config or default
  const defaultMapping: Record<string, string> = {};
  variables.forEach((v: string) => {
    defaultMapping[v] = 'name'; // Default all to name initially
  });

  const savedVariables = Array.isArray(template?.variables_config) 
    ? template.variables_config.reduce((acc: any, curr: any) => ({ ...acc, [curr.id]: curr.type }), {}) 
    : (template?.variables_config || {});

  const initialMapping: Record<string, string> = {};
  const initialCustomNames: Record<string, string> = {};

  variables.forEach((v: string) => {
    const saved = savedVariables[v];
    if (saved) {
      const isStandard = AVAILABLE_VARIABLES.some(opt => opt.value === saved);
      if (isStandard) {
        initialMapping[v] = saved;
      } else {
        initialMapping[v] = 'custom';
        initialCustomNames[v] = saved;
      }
    } else {
      initialMapping[v] = 'name';
    }
  });

  const [variableMapping, setVariableMapping] = useState<Record<string, string>>(initialMapping);
  const [customNames, setCustomNames] = useState<Record<string, string>>(initialCustomNames);

  const { data, setData, post, processing } = useForm({
    template_name: template?.template_name || details?.friendly_name || 'My Campaign Template',
    image_url: template?.image_url || '',
    variables_config: {}
  });

  // Keep variables_config in sync with form data format
  useEffect(() => {
    const finalMapping: Record<string, string> = {};
    variables.forEach((v: string) => {
      finalMapping[v] = variableMapping[v] === 'custom' ? (customNames[v] || 'custom_data') : variableMapping[v];
    });
    setData('variables_config', finalMapping);
  }, [variableMapping, customNames]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/v1/whatsapp_invitations/${invitation.id}/whatsapp_invitation_templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          whatsapp_invitation_template: {
            template_name: data.template_name,
            image_url: data.image_url,
            variables_config: data.variables_config
          }
        })
      });
      if (response.ok) {
        const locale = window.location.pathname.split('/')[1] || 'en';
        window.location.href = `/${locale}/invitations/${invitation.id}/import_users`;
      } else {
        console.error("Save failed");
      }
    } catch (error) {
      console.error("Save failed", error);
    }
  };

  // Generate preview text replacing {{1}} with dummy data based on selection
  const getPreviewText = () => {
    let text = bodyText;
    variables.forEach((v: string) => {
      const mappedField = variableMapping[v];
      let dummyValue = `[${mappedField}]`;
      
      // Provide realistic dummy data
      if (mappedField === 'name') dummyValue = 'John Doe';
      else if (mappedField === 'company') dummyValue = 'Acme Corp';
      else if (mappedField === 'event_name') dummyValue = 'Global Tech Summit 2026';
      else if (mappedField === 'rsvp_link') dummyValue = 'https://eventy.sa/rsvp/123';
      else if (mappedField === 'custom') dummyValue = `[${customNames[v] || 'Custom'}]`;
      
      text = text.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), dummyValue);
    });
    
    // Convert newlines to BR for HTML rendering
    return text.split('\\n').join('<br/>').split('\n').join('<br/>');
  };

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href={`/en/invitations`} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Configure WhatsApp Template</h1>
        </div>
        <button
          onClick={handleSubmit}
          disabled={processing}
          className="flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-2.5 rounded-lg shadow-sm font-medium transition"
        >
          <Save className="h-5 w-5" /> Save & Continue
        </button>
      </div>

      {!details ? (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Could not fetch template details. Error: {whatsappTemplateDetails?.error || 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Left Column: Configuration */}
          <div className="space-y-6">
            <div className="bg-white shadow rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b pb-4">Mapping & Media</h3>
              
              <div className="space-y-6">
                {headerFormat === 'IMAGE' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Header Image URL (Required)
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <input
                        type="url"
                        className="flex-1 rounded-md border-gray-300 focus:border-[#25D366] focus:ring-[#25D366] sm:text-sm p-2 border"
                        value={data.image_url}
                        onChange={e => setData('image_url', e.target.value)}
                        placeholder="https://example.com/header.jpg"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      This template requires an image header. You can provide a static image or a dynamically generated image URL.
                    </p>
                  </div>
                )}

                {variables.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-800 mb-4">Map Variables</h4>
                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {variables.map((v: string) => (
                        <div key={v} className="flex items-center gap-4">
                          <div className="w-16 flex-shrink-0">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {`{{${v}}}`}
                            </span>
                          </div>
                          <div className="flex-1 space-y-2">
                            <select
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366] sm:text-sm p-2 border"
                              value={variableMapping[v] || 'name'}
                              onChange={e => setVariableMapping({ ...variableMapping, [v]: e.target.value })}
                            >
                              {AVAILABLE_VARIABLES.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            {variableMapping[v] === 'custom' && (
                              <input
                                type="text"
                                placeholder="Type exact Excel column name (e.g. Table Number)"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#25D366] focus:ring-[#25D366] sm:text-sm p-2 border bg-indigo-50"
                                value={customNames[v] || ''}
                                onChange={e => setCustomNames({ ...customNames, [v]: e.target.value })}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {variables.length === 0 && headerFormat !== 'IMAGE' && (
                  <p className="text-sm text-gray-500 italic">
                    This template does not require any variables or media mapping. It is a static text template.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: WhatsApp Live Preview */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>
              
              {/* WhatsApp Mock Device */}
              <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[8px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl overflow-hidden">
                {/* Screen background (WhatsApp chat background pattern) */}
                <div className="absolute inset-0 bg-[#E5DDD5] overflow-y-auto pb-6" style={{ backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")', backgroundSize: 'cover' }}>
                  
                  {/* WhatsApp Header */}
                  <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10 shadow-md">
                    <ArrowLeft className="h-5 w-5" />
                    <div className="h-8 w-8 bg-gray-300 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden">
                      <ImageIcon className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm">Eventy Bot</h3>
                      <p className="text-xs text-gray-200">Official Business Account</p>
                    </div>
                  </div>

                  {/* Chat Bubble */}
                  <div className="mt-6 mx-4">
                    <div className="bg-white rounded-lg rounded-tl-none shadow-sm overflow-hidden w-full relative">
                      
                      {/* Header Image */}
                      {headerFormat === 'IMAGE' && (
                        <div className="w-full h-32 bg-gray-200 flex items-center justify-center overflow-hidden border-b border-gray-100">
                          {data.image_url ? (
                            <img src={data.image_url} alt="Header" className="w-full h-full object-cover" />
                          ) : (
                            <div className="flex flex-col items-center text-gray-400">
                              <ImageIcon className="h-8 w-8 mb-1" />
                              <span className="text-xs">Image Header</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Body Text */}
                      <div className="p-3 text-sm text-gray-800 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: getPreviewText() }} />

                      {/* Footer Text */}
                      {footerText && (
                        <div className="px-3 pb-2 text-xs text-gray-500">
                          {footerText}
                        </div>
                      )}

                      {/* Message Time */}
                      <div className="px-3 pb-2 text-right text-[10px] text-gray-400">
                        10:42 AM
                      </div>

                      {/* Buttons */}
                      {buttons && buttons.length > 0 && (
                        <div className="border-t border-gray-100 flex flex-col">
                          {buttons.map((btn: any, idx: number) => (
                            <div key={idx} className="py-2.5 border-b border-gray-100 last:border-0 text-center text-[#00a884] font-medium text-sm flex items-center justify-center gap-1 cursor-pointer hover:bg-gray-50">
                              {btn.type === 'URL' ? '🔗 ' : (btn.type === 'PHONE_NUMBER' ? '📞 ' : '↩️ ')}
                              {btn.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
          
        </div>
      )}
    </div>
  );
}
